import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Receipt, Loader2, RotateCcw, Link } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useQuickBooks } from "@/hooks/useQuickBooks";

interface OrganisationBillingTabProps {
  organisation: any;
}

const OrganisationBillingTab: React.FC<OrganisationBillingTabProps> = ({
  organisation,
}) => {
  // QuickBooks integration
  const { 
    isLoading: qbLoading, 
    purchaseOrders, 
    fetchPurchaseOrders, 
    connectToQuickBooks, 
    isConnected, 
    isConnecting,
    isCheckingConnection
  } = useQuickBooks(organisation.id);
  return (
    <div className="mt-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Purchase Orders</h3>
        <div className="flex gap-2">
          {!isCheckingConnection && !isConnected && (
            <Button
              onClick={connectToQuickBooks}
              disabled={isConnecting}
              variant="default"
              size="sm"
            >
              {isConnecting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Link className="h-4 w-4 mr-2" />
              )}
              {isConnecting ? 'Connecting...' : 'Connect to QuickBooks'}
            </Button>
          )}
          {isConnected && (
            <Button
              onClick={fetchPurchaseOrders}
              disabled={qbLoading}
              variant="outline"
              size="sm"
            >
              {qbLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RotateCcw className="h-4 w-4 mr-2" />
              )}
              {qbLoading ? 'Loading...' : 'Refresh Data'}
            </Button>
          )}
        </div>
      </div>

      {/* QuickBooks Purchase Orders */}
      <Card>
        <CardContent className="p-6">
          {qbLoading && purchaseOrders.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin opacity-50" />
              <p>Loading purchase orders...</p>
              <p className="text-sm mt-2">Please wait while we fetch your QuickBooks data.</p>
            </div>
          ) : purchaseOrders.length > 0 ? (
            <div className="bg-white rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Purchase Order Number</TableHead>
                    <TableHead>Purchase Order Date</TableHead>
                    <TableHead className="text-right">Purchase Price</TableHead>
                    <TableHead>Invoice Number</TableHead>
                    <TableHead>NERV</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {purchaseOrders.map((po, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{po.purchaseOrderNumber}</TableCell>
                      <TableCell>{po.purchaseOrderDate}</TableCell>
                      <TableCell className="text-right">${po.purchasePrice.toLocaleString()}</TableCell>
                      <TableCell>{po.invoiceNumber || 'N/A'}</TableCell>
                      <TableCell>{po.customFieldNERV || 'N/A'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Receipt className="w-12 h-12 mx-auto mb-4 opacity-50" />
              {isCheckingConnection ? (
                <>
                  <p>Checking QuickBooks connection...</p>
                  <p className="text-sm mt-2">Please wait while we verify your connection status.</p>
                </>
              ) : !isConnected ? (
                <>
                  <p>Connect to QuickBooks to view purchase orders.</p>
                  <p className="text-sm mt-2">Click "Connect to QuickBooks" to get started.</p>
                </>
              ) : (
                <>
                  <p>No QuickBooks purchase orders found.</p>
                  <p className="text-sm mt-2">Data will automatically load when available.</p>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OrganisationBillingTab;